import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import api from '../api';

const field: React.CSSProperties = {
  width: '100%',
  padding: '10px',
  fontSize: '16px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  boxSizing: 'border-box',
};

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Request-reset mode
  const [email, setEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  // Confirm-reset mode
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetDone, setResetDone] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { email });
      setRequestSent(true);
    } catch (err) {
      setError(
        axios.isAxiosError(err)
          ? err.response?.data?.error || 'Request failed.'
          : 'An unexpected error occurred.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password/confirm', { token, newPassword });
      setResetDone(true);
    } catch (err) {
      setError(
        axios.isAxiosError(err)
          ? err.response?.data?.error || 'Link may have expired. Request a new one.'
          : 'An unexpected error occurred.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '40px 20px', maxWidth: '400px', margin: '80px auto', fontFamily: 'Arial' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '6px' }}>Reset Password</h1>
      <p style={{ textAlign: 'center', color: '#666', marginBottom: '28px' }}>
        {token ? 'Enter your new password below.' : "Enter your email and we'll send a reset link."}
      </p>

      <div style={{ border: '1px solid #ccc', padding: '30px', borderRadius: '8px' }}>
        {/* ── Confirm mode (token in URL) ── */}
        {token && !resetDone && (
          <form onSubmit={handleConfirm}>
            {error && (
              <div style={{ background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: '4px', marginBottom: '16px', fontSize: '14px' }}>
                {error}
              </div>
            )}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>New Password</label>
              <input
                type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                required minLength={8} autoFocus style={field} placeholder="Min. 8 characters"
              />
            </div>
            <div style={{ marginBottom: '22px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Confirm Password</label>
              <input
                type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                required minLength={8} style={field} placeholder="Repeat new password"
              />
            </div>
            <button
              type="submit" disabled={loading}
              style={{ width: '100%', padding: '12px', fontSize: '16px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              {loading ? 'Resetting…' : 'Set New Password'}
            </button>
          </form>
        )}

        {/* ── Request mode (no token) ── */}
        {!token && !requestSent && (
          <form onSubmit={handleRequest}>
            {error && (
              <div style={{ background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: '4px', marginBottom: '16px', fontSize: '14px' }}>
                {error}
              </div>
            )}
            <div style={{ marginBottom: '22px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Email Address</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                required autoFocus style={field} placeholder="you@example.com"
              />
            </div>
            <button
              type="submit" disabled={loading}
              style={{ width: '100%', padding: '12px', fontSize: '16px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              {loading ? 'Sending…' : 'Send Reset Link'}
            </button>
          </form>
        )}

        {/* ── Success states ── */}
        {!token && requestSent && (
          <div style={{ textAlign: 'center', color: '#2e7d32' }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>✅</div>
            <p style={{ marginBottom: '4px', fontWeight: 'bold' }}>Check your inbox</p>
            <p style={{ color: '#555', fontSize: '14px' }}>
              If <strong>{email}</strong> is registered, a reset link has been sent.
            </p>
          </div>
        )}

        {token && resetDone && (
          <div style={{ textAlign: 'center', color: '#2e7d32' }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>✅</div>
            <p style={{ marginBottom: '4px', fontWeight: 'bold' }}>Password updated!</p>
            <p style={{ color: '#555', fontSize: '14px', marginBottom: '16px' }}>You can now sign in with your new password.</p>
            <Link
              to="/login"
              style={{ display: 'inline-block', padding: '10px 24px', backgroundColor: '#4CAF50', color: 'white', borderRadius: '4px', textDecoration: 'none', fontSize: '15px' }}
            >
              Go to Login
            </Link>
          </div>
        )}
      </div>

      {!resetDone && (
        <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '14px' }}>
          <Link to="/login" style={{ color: '#2196F3', textDecoration: 'none' }}>← Back to login</Link>
        </div>
      )}
    </div>
  );
}
